import { applyLabels } from './labels';
import { useEffect, useState } from 'react';
import { metaApi, type Meta, type MiniItem } from './api';

/**
 * Verrijkte metadata (TMDB, Fanart.tv, MDBList) met cache in de browser,
 * plus een gebundelde artwork-lader voor posters in rijen.
 */

type Ref = { type: 'movie' | 'tv'; tmdbId: number } | null;

const metaCache = new Map<string, Promise<Meta | null>>();

export function useMeta(ref: Ref): Meta | null {
  const [meta, setMeta] = useState<Meta | null>(null);
  const key = ref ? `${ref.type}:${ref.tmdbId}` : '';
  useEffect(() => {
    setMeta(null);
    if (!ref) return;
    let p = metaCache.get(key);
    if (!p) {
      p = metaApi.meta(ref.type, ref.tmdbId).catch(() => null);
      metaCache.set(key, p);
    }
    let live = true;
    void p.then((m) => live && setMeta(m));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return meta;
}

// ---------- Posterbron ----------

export type ArtSource = 'plex' | 'tmdb' | 'fanart' | 'spatial';
const ART_KEY = 'tumedia.artSource';

export function artSource(): ArtSource {
  try {
    const v = localStorage.getItem(ART_KEY);
    return v === 'tmdb' || v === 'fanart' || v === 'spatial' ? v : 'plex';
  } catch {
    return 'plex';
  }
}

export function setArtSource(v: ArtSource): void {
  try {
    localStorage.setItem(ART_KEY, v);
  } catch {
    // niet erg
  }
}

type Art = { poster: string | null; backdrop: string | null; logo: string | null };
type ExtSource = 'tmdb' | 'fanart' | 'spatial';
const artCache = new Map<string, Art | null>();
const waiting = new Map<string, ((a: Art | null) => void)[]>();
const queues: Record<ExtSource, { type: 'movie' | 'tv'; tmdbId: number }[]> = { tmdb: [], fanart: [], spatial: [] };

/** Na het vastzetten van artwork: alles opnieuw ophalen. */
export function clearArtCaches(): void {
  artCache.clear();
  metaCache.clear();
}

// ---------- Labels van SpatialPosters ----------

let spatialLabels = false;
/** SpatialPosters zet zelf labels op de poster: dan geen eigen labels (4K, HDR, Atmos) erbovenop. */
export function hideOwnFlags(): boolean {
  return spatialLabels && artSource() === 'spatial';
}
export function setSpatialLabels(on: boolean): void {
  spatialLabels = on;
  applyLabels();
}
const timers: Partial<Record<ExtSource, number>> = {};

function settle(source: ExtSource, b: { type: 'movie' | 'tv'; tmdbId: number }, a: Art | null) {
  const k = `${source}:${b.type}:${b.tmdbId}`;
  artCache.set(k, a);
  waiting.get(k)?.forEach((fn) => fn(a));
  waiting.delete(k);
}

/** Verzamelt verzoeken een paar ms en vraagt ze in één keer op (max 60 per keer). */
function flush(source: ExtSource) {
  timers[source] = undefined;
  const batch = queues[source].splice(0, 60);
  if (!batch.length) return;
  metaApi.art(source, batch)
    .then((r) => batch.forEach((b) => settle(source, b, r.art[`${b.type}:${b.tmdbId}`] ?? null)))
    .catch(() => batch.forEach((b) => settle(source, b, null)));
  if (queues[source].length) timers[source] = window.setTimeout(() => flush(source), 30);
}

/** Artwork uit TMDB of Fanart.tv volgens de gekozen posterbron; null als die Plex is. */
export function useExternalArt(ref: Ref): Art | null {
  return useArtFrom(ref, artSource());
}

/** Artwork uit een vaste bron (bijv. 'fanart' voor clearlogo's in brede tegels). */
export function useArtFrom(ref: Ref, source: ArtSource): Art | null {
  const k = ref && source !== 'plex' ? `${source}:${ref.type}:${ref.tmdbId}` : '';
  const [art, setArt] = useState<Art | null>(() => (k ? artCache.get(k) ?? null : null));
  useEffect(() => {
    if (!k || !ref || source === 'plex') return;
    const src: ExtSource = source;
    if (artCache.has(k)) {
      setArt(artCache.get(k) ?? null);
      return;
    }
    let live = true;
    const list = waiting.get(k) ?? [];
    list.push((a) => live && setArt(a));
    if (!waiting.has(k)) {
      waiting.set(k, list);
      queues[src].push(ref);
      if (!timers[src]) timers[src] = window.setTimeout(() => flush(src), 40);
    }
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k]);
  return art;
}

// ---------- Staat een TMDB-titel in de bibliotheek? ----------

const libCache = new Map<string, string | null>();

export function clearLibraryCache(): void {
  libCache.clear();
}

export function useInLibrary(items: MiniItem[]): Record<string, string | null | undefined> {
  const [found, setFound] = useState<Record<string, string | null | undefined>>({});
  const key = items.map((i) => `${i.type}:${i.tmdbId}`).join(',');
  useEffect(() => {
    if (!items.length) return;
    const known: Record<string, string | null> = {};
    const unknown = items.filter((i) => {
      const k = `${i.type}:${i.tmdbId}`;
      if (libCache.has(k)) {
        known[k] = libCache.get(k)!;
        return false;
      }
      return true;
    });
    setFound(known);
    if (!unknown.length) return;
    let live = true;
    metaApi.inLibrary(unknown.map(({ type, tmdbId, title, year }) => ({ type, tmdbId, title, year })))
      .then((r) => {
        for (const [k, v] of Object.entries(r.found)) libCache.set(k, v);
        if (live) setFound((f) => ({ ...f, ...r.found }));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return found;
}

// ---------- Achtergrond die meebeweegt met de focus ----------

const backdropListeners = new Set<(url: string | null) => void>();
let backdropUrl: string | null = null;
let backdropTimer: number | undefined;

/** Kort vertraagd, zodat snel door een rij scrollen niet elke poster laadt. */
export function setFocusBackdrop(url: string | null | undefined): void {
  window.clearTimeout(backdropTimer);
  backdropTimer = window.setTimeout(() => {
    if (url === backdropUrl) return;
    backdropUrl = url ?? null;
    backdropListeners.forEach((l) => l(backdropUrl));
  }, 180);
}

export function useFocusBackdrop(): string | null {
  const [url, setUrl] = useState(backdropUrl);
  useEffect(() => {
    backdropListeners.add(setUrl);
    return () => {
      backdropListeners.delete(setUrl);
    };
  }, []);
  return url;
}
